/**
 * What this browser calls itself, so an administrator can enrol it.
 *
 * A tablet in a trailer has no hardware serial a web page may read, and that is
 * a deliberate property of browsers rather than a gap to work around: every
 * honest fingerprint is either weak enough to collide or strong enough to
 * follow somebody between sites. So the identifier is not discovered at all. It
 * is *minted here on first use and kept*, which makes it exactly what enrolment
 * needs — a name for this browser on this tablet that nobody else has, that
 * means nothing anywhere else, and that an administrator can read off the
 * screen and admit.
 *
 * Two consequences worth stating rather than discovering:
 *
 * - **Clearing site data un-enrols the tablet.** The identifier is gone, a new
 *   one is minted, and the old enrolment names a browser that no longer exists.
 *   That is the correct direction to fail in — the alternative is a tablet that
 *   keeps its admission through a factory reset — and the fix is to enrol it
 *   again, which takes an administrator ten seconds.
 * - **Two browsers on one tablet are two devices.** Also correct: enrolment is
 *   what makes the crew list safe to show before anybody has proved anything,
 *   and a second browser is a second thing that would be showing it.
 *
 * The value is random and carries nothing. It is not derived from the person,
 * the project or the machine, so it identifies the tablet to this instance and
 * says nothing about it to anybody else.
 */

const STORAGE_KEY = 'sitefocus.cm.device'

/** Long enough not to collide, short enough to read down a phone line. */
function mint(): string {
  const random =
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  return `dev-${random.replace(/-/g, '').slice(0, 24)}`
}

/**
 * This browser's identifier, minted on the first call and stable after it.
 *
 * A browser that refuses storage gets a fresh one every call, which means it
 * can never be enrolled. The keypad says so rather than looping: an identifier
 * that changes between the screen and the enrolment is worse than no
 * identifier, because somebody would enrol it and it would still not work.
 */
export function hardwareId(): string {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored) return stored
    const minted = mint()
    window.localStorage.setItem(STORAGE_KEY, minted)
    return minted
  } catch {
    return mint()
  }
}

/** Whether this browser can hold an identifier at all. */
export function canRememberDevice(): boolean {
  try {
    const probe = `${STORAGE_KEY}.probe`
    window.localStorage.setItem(probe, '1')
    window.localStorage.removeItem(probe)
    return true
  } catch {
    return false
  }
}

/**
 * Forget this browser's identifier.
 *
 * Only for a test that needs two devices in one browser. Nothing on a screen
 * calls it: an identifier a tablet can discard is an enrolment a tablet can
 * discard, and that is an administrator's decision made in the wrong place.
 */
export function forgetDevice(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* nothing to forget */
  }
}
